/**
 * 앱이 공유로 받은 링크를 어느 화면으로 보낼지 정한다.
 *
 * 앱은 링크 종류를 가리지 않고 /share?url=...(&text=...) 로 보낸다.
 * 무엇을 받을지는 여기서 정한다 — 받는 범위를 넓힐 때 앱을 다시 올리지
 * 않아도 되게 하려는 것이다 (앱 share_intent_handler.dart 주석 참고).
 *
 *   instagram  게시물·릴 → /share/instagram
 *   map        네이버지도·카카오맵 → /share/map (장소 가져오기)
 *   null       그 밖 → '지원하지 않는 링크'
 */

export type SharedLinkKind = 'instagram' | 'map'

/**
 * 주소에 쓸 수 있는 글자만 (RFC 3986). 앱·서버(fetchSharedList)와 같은 규칙.
 * '공백 전까지 전부'로 자르면 붙여넣기가 겹쳤을 때 뒤 글자가 따라붙는다.
 */
const URL_PATTERN = /https?:\/\/[A-Za-z0-9\-._~:/?#@!$&'()*+,;=%]+/gi

/** 글에서 첫 http(s) 주소. 문장 끝 부호·닫는 괄호는 뗀다. 없으면 null. */
export function firstUrlIn(text: string | null | undefined): string | null {
  if (!text) return null
  const re = new RegExp(URL_PATTERN.source, 'gi')
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const raw = m[0].replace(/[.,;:!?)\]}'"]+$/, '')
    try {
      if (new URL(raw).host) return raw
    } catch {
      // 다음 후보
    }
  }
  return null
}

/** 네이버·카카오 지도 공유가 주는 주소의 host. 장소 가져오기(resolveSharedLink)가 받는다. */
const MAP_HOSTS = [
  'naver.me',
  'map.naver.com',
  'booking.map.naver.com',
  'kko.to',
  'map.kakao.com',
]

function hostMatches(host: string, base: string): boolean {
  return host === base || host.endsWith(`.${base}`)
}

export function sharedLinkKind(url: string): SharedLinkKind | null {
  let u: URL
  try {
    u = new URL(url)
  } catch {
    return null
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null

  const host = u.hostname.toLowerCase()

  // 게시물·릴만. 프로필이나 탐색 화면은 담을 글이 아니다 (앱 instagramUrlFrom 과 같다)
  if (hostMatches(host, 'instagram.com')) {
    return /\/(p|reel|reels|tv)\/[A-Za-z0-9_-]{5,}/.test(u.pathname)
      ? 'instagram'
      : null
  }

  // map.naver.com·map.kakao.com 은 m. 같은 하위 도메인도 받는다
  if (MAP_HOSTS.some((base) => hostMatches(host, base))) return 'map'

  return null
}
